/**
 * WindSystem - Handles wind zone mechanics
 * 
 * Creates directional wind zones that push the player.
 * 
 * @module systems/WindSystem
 */

import Phaser from 'phaser';
import BaseSystem from './BaseSystem.js';

export default class WindSystem extends BaseSystem {
    constructor(scene) {
        super(scene);
    }

    /**
     * Create wind zones from level config
     * @param {Object} config - Level configuration
     */
    create(config) {
        if (!config.windZones) return;

        config.windZones.forEach(w => {
            const zone = {
                x: w.x,
                y: w.y,
                w: w.w,
                h: w.h,
                direction: w.direction,
                force: w.force
            };

            // Wind zone indicator
            const indicator = this.scene.add.rectangle(
                w.x + w.w / 2, 
                w.y + w.h / 2, 
                w.w, 
                w.h, 
                0x00ffff, 
                0.1
            );
            indicator.setStrokeStyle(2, 0x00ffff, 0.5);

            // Animated wind particles
            const particles = [];
            const lineCount = Math.floor(w.w / 40);
            for (let i = 0; i < lineCount; i++) {
                const lineX = w.x + 20 + (i * 40);
                const particle = this.createWindParticle(lineX, w.y + w.h, w.h, w.direction);
                particles.push(particle);
            }

            this.items.push({ 
                ...zone, 
                visual: indicator,
                particles 
            });
        });

        this.isActive = this.items.length > 0;
    }

    /**
     * Create animated wind particle
     * @param {number} x - X position
     * @param {number} baseY - Base Y position
     * @param {number} height - Zone height
     * @param {string} direction - Wind direction
     * @returns {Phaser.GameObjects.Arc}
     */
    createWindParticle(x, baseY, height, direction) {
        const particle = this.scene.add.circle(x, baseY, 4, 0x00ffff, 0.8);
        
        const targetY = direction === 'up' ? baseY - height : baseY + height;
        
        this.scene.tweens.add({
            targets: particle,
            y: targetY,
            alpha: 0,
            duration: 1500,
            repeat: -1,
            delay: Phaser.Math.Between(0, 1000)
        });

        return particle;
    }

    /**
     * Apply wind forces to player
     * @param {number} delta - Delta time
     * @param {Object} player - Player reference
     */
    update(delta, player) {
        if (!this.isActive || !player || !player.sprite) return;

        const playerX = player.sprite.x;
        const playerY = player.sprite.y;

        this.items.forEach(zone => {
            // Check if player is inside wind zone
            if (playerX >= zone.x && playerX <= zone.x + zone.w &&
                playerY >= zone.y && playerY <= zone.y + zone.h) {
                
                let forceX = 0;
                let forceY = 0;

                switch (zone.direction) {
                    case 'up':
                        forceY = -zone.force;
                        break;
                    case 'down':
                        forceY = zone.force;
                        break;
                    case 'left':
                        forceX = -zone.force;
                        break;
                    case 'right':
                        forceX = zone.force;
                        break;
                }

                this.scene.matter.body.applyForce(player.sprite.body, {
                    x: player.sprite.x,
                    y: player.sprite.y
                }, { x: forceX, y: forceY });
            }
        });
    }

    /**
     * Cleanup
     */
    destroy() {
        this.items.forEach(zone => {
            if (zone.particles) {
                zone.particles.forEach(p => p.destroy());
            }
        });
        super.destroy();
    }
}
